import React from "react";
import ReactMarkdown from "react-markdown";
import { Accordion, AccordionButton, AccordionItem, AccordionPanel, Text, Flex, Button } from "@chakra-ui/react";
import { Section, sectionSpacingY } from "components/section";
import { ChevronDownIcon } from "ui/icons";
import { Link } from "ui/link";
import { integrationsCount } from "../../constants/integrations";

const faqItems = [
  {
    question: "What is a destination?",
    answer: `A destination is any of the ${integrationsCount}+ integrations that Hightouch lets you sync data to. Multiple instances of the same destination will only be counted once. Read [our documentation](/docs/pricing/ss-pricing) to learn more about [what counts as a destination](/docs/pricing/ss-pricing#destinations).`,
  },
  {
    question: "Can I try a new destination before being charged for it?",
    answer:
      "Absolutely! Beyond your first free destination, each destination has a 30 day free trial before you are charged for it.",
  },
  {
    question: "Do you offer self-service billing?",
    answer:
      "Yes! We fully support in-app self-service billing for our Free, Starter, and Pro plans. We let you manage your plan, explore your usage, and view invoices directly through our UI. Learn more in our [self-serve pricing documentation](/docs/pricing/ss-pricing).",
  },
  {
    question: "Can I pay monthly instead of annually?",
    answer:
      "We recommend customers to pay annually. Annual billing includes a 20% discount. However, we also support monthly billing for our self-service plans.",
  },
  {
    question: "Can I ask for help setting up Hightouch even if I'm on the Free plan?",
    answer:
      "Yes. We're happy to help customers regardless of their size. Please message us on Intercom, email us, or book a time with us and we'll make sure set up is effortless as possible.",
  },
  {
    question: "Do you have special pricing for early stage startups or businesses affected by Covid?",
    answer:
      "For early stage startups (<40 employees or <$10M in funding), we offer six months of our Starter plan for free. For business affected by Covid, please reach out to us and we'll be happy to assess on a case by case basis.",
  },
  {
    question: "What if I have a billing-related question?",
    answer:
      "For questions related to payments processing, invoices, discounts, overages, or any other billing-related topics, please message us on Intercom or email us, and we will help you resolve the question.",
  },
];

export function Faq() {
  return (
    <Section my={0} pb={sectionSpacingY}>
      <Text as="h2" textStyle="h4" textAlign="center" mb={{ base: 8, lg: 16 }}>
        Frequently asked questions
      </Text>
      <Accordion allowToggle bg="white">
        {faqItems.map((faq) => (
          <AccordionItem key={faq.question} borderTop={0} borderBottom="1px solid" borderBottomColor="base.border">
            {({ isExpanded }) => (
              <>
                <AccordionButton aria-label={faq.question} py={4} px={0} role="group" _hover={{ bg: "white" }}>
                  <Flex justify="space-between" w="full" align="center" textAlign="left" gap={4}>
                    <Text fontSize="lg" fontWeight="semibold">
                      {faq.question}
                    </Text>
                    <Button
                      as="div"
                      variant="white"
                      bg={isExpanded ? "rgba(255, 255, 255, 0.9)" : "white"}
                      _groupHover={{ bg: "rgba(255, 255, 255, 0.9)" }}
                      transition="all .15s ease"
                      p={1}
                    >
                      <ChevronDownIcon
                        boxSize={7}
                        color="text.primary"
                        transform={isExpanded ? "rotate(180deg)" : "none"}
                        transition="transform 300ms ease"
                      />
                    </Button>
                  </Flex>
                </AccordionButton>
                <AccordionPanel
                  color="text.secondary"
                  fontSize={{ base: "md", md: "lg" }}
                  pb={10}
                  pt={0}
                  px={0}
                  maxW="5xl"
                  mr={{ base: 0, md: 10 }}
                >
                  <ReactMarkdown
                    components={{
                      a: ({ node, href, ...props }) => <Link href={href} variant="article" size="lg" {...props} />,
                    }}
                  >
                    {faq.answer}
                  </ReactMarkdown>
                </AccordionPanel>
              </>
            )}
          </AccordionItem>
        ))}
      </Accordion>
    </Section>
  );
}
